/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI Floating Bubble Menu Component (Task 10.2.2 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/core';
import {
  Sparkles,
  ArrowRight,
  Minimize2,
  Maximize2,
  Smile,
  ShieldCheck,
  Send,
  Square,
  ChevronDown,
  X,
} from 'lucide-react';
import {
  AI_TONE_PRESETS,
  type AiActionType,
} from './ai-actions';
import { aiEditorStreamService } from '../../../services/ai-editor-stream.service';
import { useI18n } from '../../../stores/useI18nStore';

export interface FloatingAIBubbleMenuProps {
  editor: Editor | null;
}

export const FloatingAIBubbleMenu: React.FC<FloatingAIBubbleMenuProps> = ({ editor }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingTokenCount, setStreamingTokenCount] = useState(0);
  const { t } = useI18n();

  // Sub-menus
  const [showToneDropdown, setShowToneDropdown] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customPromptText, setCustomPromptText] = useState('');

  const menuRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!editor || editor.isDestroyed) {
      setIsVisible(false);
      return;
    }

    const { selection } = editor.state;
    if (selection.empty) {
      if (!isStreaming) {
        setIsVisible(false);
        setShowToneDropdown(false);
        setShowCustomInput(false);
      }
      return;
    }

    // Get selected text
    const text = editor.state.doc.textBetween(selection.from, selection.to, ' ');
    if (!text.trim()) {
      if (!isStreaming) setIsVisible(false);
      return;
    }

    setSelectedText(text);

    // Compute coordinates of selection start
    try {
      const fromCoords = editor.view.coordsAtPos(selection.from);
      const toCoords = editor.view.coordsAtPos(selection.to);

      const menuWidth = 460;
      const left = Math.min(
        Math.max(16, (fromCoords.left + toCoords.left) / 2 - menuWidth / 2),
        window.innerWidth - menuWidth - 24,
      );
      const top = Math.max(12, fromCoords.top - 54);

      setPosition({ top, left });
      setIsVisible(true);
    } catch {
      setIsVisible(false);
    }
  }, [editor, isStreaming]);

  useEffect(() => {
    if (!editor) return;

    editor.on('selectionUpdate', updateMenuPosition);
    editor.on('blur', () => {
      setTimeout(() => {
        if (!isStreaming && !menuRef.current?.contains(document.activeElement)) {
          setIsVisible(false);
        }
      }, 200);
    });

    return () => {
      editor.off('selectionUpdate', updateMenuPosition);
    };
  }, [editor, updateMenuPosition, isStreaming]);

  const handleAction = async (action: AiActionType, toneId?: string) => {
    if (!editor || !selectedText) return;

    setShowToneDropdown(false);
    setIsStreaming(true);
    setStreamingTokenCount(0);

    try {
      await aiEditorStreamService.executeInEditor({
        editor,
        action,
        selectedText,
        toneId,
        customPrompt: action === 'custom-prompt' ? customPromptText : undefined,
        onToken: () => {
          setStreamingTokenCount((c) => c + 1);
        },
        onDone: () => {
          setIsStreaming(false);
          setIsVisible(false);
          setShowCustomInput(false);
          setCustomPromptText('');
        },
        onError: () => {
          setIsStreaming(false);
        },
      });
    } catch {
      setIsStreaming(false);
    }
  };

  const handleStopStream = () => {
    aiEditorStreamService.abort();
    setIsStreaming(false);
  };

  if (!isVisible && !isStreaming) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}
      className="flex flex-col gap-1.5 rounded-2xl border border-primary/30 bg-surface/95 p-1.5 shadow-glow-primary/20 backdrop-blur-xl animate-scale-in text-foreground"
    >
      {/* Active Streaming Overlay Bar */}
      {isStreaming ? (
        <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-primary/10 rounded-xl text-xs">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <span className="w-2.5 h-2.5 rounded-full bg-primary animate-ping" />
            <span>AI Copilot...</span>
            <span className="font-mono text-[11px] px-1.5 py-0.2 rounded bg-primary/20">
              {streamingTokenCount} tokens
            </span>
          </div>
          <button
            type="button"
            onClick={handleStopStream}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-500 text-white font-bold text-[11px] hover:bg-rose-600 transition-colors shadow-sm"
          >
            <Square className="w-3 h-3 fill-current" />
            <span>{t('editor.bubble.stopStreaming')}</span>
          </button>
        </div>
      ) : (
        <>
          {/* Default Actions Bar */}
          <div className="flex items-center gap-1 text-xs">
            {/* Brand Copilot Pill */}
            <div className="flex items-center gap-1 px-2 py-1 text-primary font-bold border-r border-surface-border pr-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>AI</span>
            </div>

            {/* Quick 1-click action buttons */}
            <button
              type="button"
              onClick={() => handleAction('continue')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted font-medium transition-colors"
              title={t('editor.bubble.actions.continueDesc')}
            >
              <ArrowRight className="w-3.5 h-3.5 text-primary" />
              <span>{t('editor.bubble.actions.continue')}</span>
            </button>

            <button
              type="button"
              onClick={() => handleAction('summarize')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted font-medium transition-colors"
              title={t('editor.bubble.actions.summarizeDesc')}
            >
              <Minimize2 className="w-3.5 h-3.5 text-accent" />
              <span>{t('editor.bubble.actions.summarize')}</span>
            </button>

            <button
              type="button"
              onClick={() => handleAction('expand')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted font-medium transition-colors"
              title={t('editor.bubble.actions.expandDesc')}
            >
              <Maximize2 className="w-3.5 h-3.5 text-emerald-500" />
              <span>{t('editor.bubble.actions.expand')}</span>
            </button>

            {/* Tone Dropdown Trigger */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowToneDropdown(!showToneDropdown);
                  setShowCustomInput(false);
                }}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg font-medium transition-colors ${
                  showToneDropdown
                    ? 'bg-primary/15 text-primary'
                    : 'text-foreground-muted hover:text-foreground hover:bg-muted'
                }`}
              >
                <Smile className="w-3.5 h-3.5 text-amber-500" />
                <span>{t('editor.bubble.actions.changeTone')}</span>
                <ChevronDown className="w-3 h-3" />
              </button>

              {showToneDropdown && (
                <div className="absolute top-full left-0 mt-1.5 z-40 w-44 rounded-xl border border-surface-border bg-surface p-1 shadow-glass text-xs space-y-0.5 animate-scale-in">
                  {AI_TONE_PRESETS.map((tone) => {
                    const toneLabel = t(
                      `editor.bubble.tones.${tone.id}` as any,
                      undefined,
                      tone.name,
                    );
                    return (
                      <button
                        key={tone.id}
                        type="button"
                        onClick={() => handleAction('change-tone', tone.id)}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left hover:bg-muted transition-colors"
                      >
                        <span>{toneLabel}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleAction('grammar-fix')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted font-medium transition-colors"
              title={t('editor.bubble.actions.grammarFixDesc')}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-accent" />
              <span>{t('editor.bubble.actions.grammarFix')}</span>
            </button>

            {/* Ask AI Custom Prompt Trigger */}
            <button
              type="button"
              onClick={() => {
                setShowCustomInput(!showCustomInput);
                setShowToneDropdown(false);
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg font-medium transition-colors ${
                showCustomInput
                  ? 'bg-primary text-primary-fg'
                  : 'text-foreground-muted hover:text-foreground hover:bg-muted'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>{t('editor.bubble.actions.customPrompt')}</span>
            </button>
          </div>

          {/* Expandable Custom Prompt Input */}
          {showCustomInput && (
            <div className="flex items-center gap-2 pt-1 border-t border-surface-border px-1">
              <input
                type="text"
                value={customPromptText}
                onChange={(e) => setCustomPromptText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customPromptText.trim()) {
                    handleAction('custom-prompt');
                  }
                  if (e.key === 'Escape') {
                    setShowCustomInput(false);
                  }
                }}
                placeholder={t('editor.bubble.insertPromptPlaceholder')}
                autoFocus
                className="flex-1 bg-surface-elevated px-2.5 py-1.5 rounded-lg text-xs border border-surface-border focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                onClick={() => handleAction('custom-prompt')}
                disabled={!customPromptText.trim()}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover disabled:opacity-40 transition-colors"
              >
                {t('editor.bubble.apply')}
              </button>
              <button
                type="button"
                onClick={() => setShowCustomInput(false)}
                className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted"
                aria-label={t('common.close')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
